import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import PolicyDetailView from '../components/PolicyDetailView';

const PolicyDetail = () => {
  // Extract slug from URL parameter
  const { slug: slugWithHtml } = useParams();
  const fullSlug = slugWithHtml ? slugWithHtml.replace(/\.html$/, '') : '';
  
  // Extract ID from the end of the slug (format: slug-ID)
  const slugMatch = fullSlug.match(/^(.+)-(\d+)$/);
  const slug = slugMatch ? slugMatch[1] : fullSlug; // The slug without the ID
  const id = slugMatch ? slugMatch[2] : null;       // The numeric ID
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch policy data on component mount
  useEffect(() => {
    const fetchPolicy = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // If ID is available, use it (more reliable), otherwise use slug
        let fetchUrl;
        if (id) {
          console.log(`Fetching policy with ID: ${id}`);
          fetchUrl = `/api/policies/${id}`;
        } else {
          console.log(`Fetching policy with slug: ${slug}`);
          fetchUrl = `/api/policies/slug/${slug}`;
        }
        
        const response = await fetch(fetchUrl);
        
        if (!response.ok) {
          console.error(`API returned error status: ${response.status} ${response.statusText}`);
          throw new Error(`Error fetching policy: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log(`API response received:`, data);
        
        if (data.success) {
          console.log(`Successfully loaded policy: ${data.policy.title} (ID: ${data.policy.id}, Slug: ${data.policy.slug})`);
          setPolicy(data.policy);
        } else {
          console.error(`API reported failure: ${data.error}`);
          throw new Error(data.error || 'Failed to fetch policy');
        }
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError(err.message || 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    };
    
    if (id || slug) {
      fetchPolicy();
    } else {
      console.error('No ID or slug provided to PolicyDetail component');
      setError('Missing policy identifier');
      setLoading(false);
    }
  }, [id, slug]);
  
  // Get meta description based on policy data
  const getMetaDescription = () => {
    if (!policy) return 'Policy analysis with democratic threat assessment';
    
    let description = `Analysis of "${policy.title}" with a democratic threat score of ${policy.score}/100`;
    
    if (policy.score >= 81) {
      description += ' (Severe threat level)';
    } else if (policy.score >= 61) {
      description += ' (Significant threat level)';
    } else if (policy.score >= 41) {
      description += ' (Moderate threat level)';
    } else if (policy.score >= 21) {
      description += ' (Mild threat level)';
    } else {
      description += ' (Minimal threat level)';
    }
    
    return description;
  };
  
  return (
    <div className="w-full">
      <Helmet>
        <title>{policy ? `${policy.title} | Democracy Threat Level` : 'Policy Analysis | Democracy Threat Level'}</title>
        <meta name="description" content={getMetaDescription()} />
        {policy && (
          <>
            <meta property="og:title" content={`${policy.title} | Democracy Threat Level`} />
            <meta property="og:description" content={getMetaDescription()} />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="article" />
            <meta property="article:published_time" content={policy.date} />
          </>
        )}
      </Helmet>
      
      
      <PolicyDetailView policyId={id} policySlug={slug} />
    </div>
  );
};

export default PolicyDetail;